import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  MessageSquare,
  Users,
  Calendar,
  Mail,
  FileText,
  PenTool,
  Clock,
  DollarSign,
  Rocket,
  Brain,
  Download,
  Share2,
  Flag,
  ChartBar
} from "lucide-react";
import { cn } from "@/lib/utils";

interface TaskMetric {
  icon: any;
  title: string;
  description: string;
  sliderRange: [number, number];
  timePerUnit: number;
  aiSavingsPercent: number;
  unit: string;
  defaultValue: number;
  frequency: "per week" | "per month";
}

interface CalculatedMetrics {
  totalHoursPerMonth: number;
  timeSavedHours: number;
  monthlyCost: number;
  annualSavings: number;
  additionalImpact: number;
}

interface ImpactMetricProps {
  icon: any;
  title: string;
  value: string;
  subtitle: string;
}

interface NonprofitImpactCalculatorProps {
  showShareAndPdf?: boolean;
}

const taskMetrics: TaskMetric[] = [
  {
    icon: MessageSquare,
    title: "Inbound Queries",
    description: "Volunteer & corporate inquiries",
    sliderRange: [0, 500],
    timePerUnit: 15, // minutes
    aiSavingsPercent: 90,
    unit: "inquiries",
    defaultValue: 100,
    frequency: "per month"
  },
  {
    icon: Users,
    title: "Corporate Bookings",
    description: "Processing & coordinating corporate requests",
    sliderRange: [0, 50],
    timePerUnit: 45, // minutes
    aiSavingsPercent: 75,
    unit: "bookings",
    defaultValue: 10,
    frequency: "per month"
  },
  {
    icon: Calendar,
    title: "Event Planning & Execution",
    description: "Community & corporate event management",
    sliderRange: [0, 30],
    timePerUnit: 480, // minutes (8 hours)
    aiSavingsPercent: 60,
    unit: "events",
    defaultValue: 4,
    frequency: "per month"
  },
  {
    icon: PenTool,
    title: "Content Creation",
    description: "Social media, thank you emails, newsletters",
    sliderRange: [0, 100],
    timePerUnit: 30, // minutes
    aiSavingsPercent: 80,
    unit: "pieces",
    defaultValue: 40,
    frequency: "per month"
  },
  {
    icon: FileText,
    title: "Grant Proposals",
    description: "Writing and submitting grant applications",
    sliderRange: [0, 20],
    timePerUnit: 360, // minutes (6 hours)
    aiSavingsPercent: 70,
    unit: "proposals",
    defaultValue: 3,
    frequency: "per month"
  },
  {
    icon: Mail,
    title: "Volunteer Communication",
    description: "Pre, during, and post-event follow-ups",
    sliderRange: [0, 1000],
    timePerUnit: 8, // minutes
    aiSavingsPercent: 85,
    unit: "communications",
    defaultValue: 200,
    frequency: "per month"
  },
  {
    icon: Flag,
    title: "Campaign Planning",
    description: "Earth Day, Pride Month, etc.",
    sliderRange: [0, 10],
    timePerUnit: 960, // minutes (16 hours)
    aiSavingsPercent: 65,
    unit: "campaigns",
    defaultValue: 1,
    frequency: "per month"
  },
  {
    icon: ChartBar,
    title: "Impact Reporting",
    description: "Post-event reports & attendance tracking",
    sliderRange: [0, 50],
    timePerUnit: 120, // minutes (2 hours)
    aiSavingsPercent: 75,
    unit: "reports",
    defaultValue: 8,
    frequency: "per month"
  }
];

export default function NonprofitImpactCalculator({ showShareAndPdf = true }: NonprofitImpactCalculatorProps) {
  const [taskValues, setTaskValues] = useState<{ [key: string]: number }>(
    Object.fromEntries(taskMetrics.map(metric => [metric.title, metric.defaultValue]))
  );

  const calculateMetrics = (): CalculatedMetrics => {
    let totalMinutesPerMonth = 0;
    taskMetrics.forEach(metric => {
      const value = taskValues[metric.title];
      const minutesPerMonth = value * metric.timePerUnit;
      totalMinutesPerMonth += minutesPerMonth;
    });

    const totalHoursPerMonth = totalMinutesPerMonth / 60;
    const timeSavedHours = totalHoursPerMonth * 0.75; // Average 75% time savings across all tasks
    const monthlyCost = totalHoursPerMonth * 35; // $35/hour average nonprofit rate
    const annualSavings = (monthlyCost * 0.75) * 12; // 75% cost reduction annually
    const additionalImpact = Math.round(timeSavedHours * 0.6); // Estimate additional mission impact hours

    return {
      totalHoursPerMonth: Math.round(totalHoursPerMonth),
      timeSavedHours: Math.round(timeSavedHours),
      monthlyCost: Math.round(monthlyCost),
      annualSavings: Math.round(annualSavings),
      additionalImpact
    };
  };

  const metrics = calculateMetrics();

  const generateReport = () => {
    // Create report content
    const reportContent = {
      title: "Your Nonprofit Impact Report",
      date: new Date().toLocaleDateString(),
      metrics: metrics,
      taskBreakdown: Object.entries(taskValues).map(([task, value]) => ({
        task,
        value,
        timeSpent: value * (taskMetrics.find(m => m.title === task)?.timePerUnit || 0) / 60,
        potentialSavings: value * (taskMetrics.find(m => m.title === task)?.timePerUnit || 0) / 60 * 0.75
      }))
    };

    // Convert to PDF or downloadable format
    // This is a placeholder - you'll need to implement actual PDF generation
    const blob = new Blob([JSON.stringify(reportContent, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'nonprofit-impact-report.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const shareResults = () => {
    const text = `I could save ${metrics.timeSavedHours} hours monthly and $${metrics.annualSavings.toLocaleString()} annually with CauseConnect's AI tools! 🚀`;
    const url = window.location.href;

    // Share options
    const shareData = {
      title: 'CauseConnect Impact Calculator',
      text,
      url
    };

    if (navigator.share) {
      navigator.share(shareData).catch(console.error);
    } else {
      // Fallback sharing options
      const shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const ImpactMetric = ({ icon: Icon, title, value, subtitle }: ImpactMetricProps) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      <Card className={cn(
        "p-6 text-center h-full border-2",
        title.includes("Save") ? "border-[#2E7D32]" : "border-transparent"
      )}>
        <div className="flex items-center justify-center mb-4">
          <div className="w-12 h-12 rounded-full bg-[#2E7D32]/10 flex items-center justify-center">
            <Icon className="w-6 h-6 text-[#2E7D32]" />
          </div>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
        <p className="text-2xl font-bold text-[#2E7D32] mb-2">{value}</p>
        <p className="text-sm text-gray-600">{subtitle}</p>
      </Card>
    </motion.div>
  );

  return (
    <div className="py-8 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              
            </h2>
            <p className="text-sm text-gray-600">
              
            </p>
          </div>

          {/* Main Calculator Container */}
          <Card className="p-6 mb-6">
            <div className="mb-4 text-sm text-gray-600 italic text-center">
              * All sliders represent monthly numbers
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {taskMetrics.map((metric) => (
                <div key={metric.title} className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#2E7D32]/10 flex items-center justify-center flex-shrink-0">
                      <metric.icon className="w-4 h-4 text-[#2E7D32]" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 text-sm">{metric.title}</h3>
                      <p className="text-xs text-gray-600">{metric.description}</p>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>0 {metric.unit}</span>
                      <span>{taskValues[metric.title]} {metric.unit}</span>
                      <span>{metric.sliderRange[1]}+ {metric.unit}</span>
                    </div>
                    <div className="flex gap-4 items-center">
                      <div className="flex-1">
                        <Slider
                          defaultValue={[metric.defaultValue]}
                          max={metric.sliderRange[1]}
                          step={1}
                          onValueChange={(values) => setTaskValues({ ...taskValues, [metric.title]: values[0] })}
                        />
                      </div>
                      <div className="text-xs text-[#2E7D32] whitespace-nowrap">
                        <Rocket className="w-3 h-3 inline-block mr-1" />
                        Save {metric.aiSavingsPercent}%
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Impact Metrics */}
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="p-4 text-center">
              <div className="text-sm text-gray-600">Monthly Time Investment</div>
              <div className="text-2xl font-bold text-[#2E7D32]">{metrics.totalHoursPerMonth} hrs</div>
              <div className="text-xs text-gray-500">Current manual hours</div>
            </Card>

            <Card className="p-4 text-center">
              <div className="text-sm text-gray-600">Time You Could Save</div>
              <div className="text-2xl font-bold text-[#2E7D32]">{metrics.timeSavedHours} hrs/mo</div>
              <div className="text-xs text-gray-500">With AI automation</div>
            </Card>

            <Card className="p-4 text-center">
              <div className="text-sm text-gray-600">Annual Cost Savings</div>
              <div className="text-2xl font-bold text-[#2E7D32]">${metrics.annualSavings.toLocaleString()}</div>
              <div className="text-xs text-gray-500">Based on nonprofit rates</div>
            </Card>

            <Card className="p-4 text-center bg-[#2E7D32]/5 border-[#2E7D32]">
              <div className="text-sm text-gray-600">Overall Efficiency Gain</div>
              <div className="text-2xl font-bold text-[#2E7D32]">80%</div>
              <div className="text-xs text-gray-500">Average time savings</div>
            </Card>
          </div>

          <div className="mt-6 flex gap-4 justify-center">
            <Button 
              className="bg-[#2E7D32] hover:bg-[#2E7D32]/90 text-sm py-5 px-8"
              onClick={() => window.location.href = '/signup'}
            >
              Start Saving Today – For Free
            </Button>
            
            {showShareAndPdf && (
              <div className="flex gap-4 justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-sm"
                  onClick={generateReport}
                >
                  <Download className="w-3 h-3 mr-1" />
                  Download Report
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-sm"
                  onClick={shareResults}
                >
                  <Share2 className="w-3 h-3 mr-1" />
                  Share Results
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
